import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Grid,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  IconButton,
  Stack,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import PhotoLibraryIcon from "@mui/icons-material/PhotoLibrary";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import api from "../services/api";

// Backend serves /uploads at root (not /api). Strip the /api segment for image URLs.
function uploadUrl(filename) {
  if (!filename) return "";
  const apiBase = api.defaults.baseURL || "";
  const host = apiBase.replace(/\/api\/?$/, "");
  return `${host}/uploads/${filename}`;
}

const emptyForm = { Wire_ID: null, WireName: "", WirePercentage: "", Notes: "" };

export default function WireLibrary() {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [form, setForm] = useState(emptyForm);
  const [galleryWire, setGalleryWire] = useState(null); // { Wire_ID, WireName }

  const fetchRows = useCallback(async () => {
    try {
      const res = await api.get("/wireLibrary", { params: { name: search } });
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Failed to load wires:", err);
    }
  }, [search]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const saveWire = async () => {
    if (!form.WireName.trim()) {
      alert("Wire name is required");
      return;
    }
    try {
      const payload = {
        WireName: form.WireName.trim(),
        WirePercentage: form.WirePercentage === "" ? null : Number(form.WirePercentage),
        Notes: form.Notes,
      };
      if (form.Wire_ID) {
        await api.put(`/wireLibrary/${form.Wire_ID}`, payload);
      } else {
        await api.post("/wireLibrary", payload);
      }
      setForm(emptyForm);
      fetchRows();
    } catch (err) {
      console.error("Save wire failed:", err);
      alert("Failed to save: " + (err.response?.data?.error || err.message));
    }
  };

  const editWire = (row) => {
    setForm({
      Wire_ID: row.Wire_ID,
      WireName: row.WireName || "",
      WirePercentage: row.WirePercentage == null ? "" : String(row.WirePercentage),
      Notes: row.Notes || "",
    });
  };

  const deleteWire = async (id) => {
    if (!window.confirm("Delete this wire and all its photos?")) return;
    try {
      await api.delete(`/wireLibrary/${id}`);
      fetchRows();
    } catch (err) {
      console.error("Delete failed:", err);
      alert("Failed to delete");
    }
  };

  return (
    <Box p={1}>
      {/* === Editor === */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6">{form.Wire_ID ? "Edit Wire" : "Add Wire to Library"}</Typography>
        <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
          Save reference photos of each wire type. The more angles/lighting you upload per wire,
          the more accurate the comparison will be.
        </Typography>

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Wire Name"
              fullWidth
              value={form.WireName}
              onChange={(e) => setForm({ ...form, WireName: e.target.value })}
              placeholder="e.g. Romex 12/2 Yellow"
            />
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <TextField
              type="number"
              label="Wire %"
              fullWidth
              value={form.WirePercentage}
              onChange={(e) => setForm({ ...form, WirePercentage: e.target.value })}
              inputProps={{ step: "0.01", min: "0", max: "100" }}
              placeholder="e.g. 65"
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <TextField
              label="Notes (optional)"
              fullWidth
              multiline
              minRows={1}
              maxRows={3}
              value={form.Notes}
              onChange={(e) => setForm({ ...form, Notes: e.target.value })}
            />
          </Grid>
        </Grid>

        <Box mt={2}>
          <Button variant="contained" onClick={saveWire}>
            {form.Wire_ID ? "Update" : "Save"}
          </Button>
          {form.Wire_ID && (
            <Button sx={{ ml: 2 }} onClick={() => setForm(emptyForm)}>Cancel</Button>
          )}
        </Box>
      </Paper>

      {/* === List === */}
      <Paper sx={{ p: 2, mb: 1 }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }}>
          <TextField
            label="Search wires"
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ flexGrow: 1 }}
          />
          <Button variant="contained" onClick={fetchRows}>Search</Button>
        </Stack>
      </Paper>

      <TableContainer component={Paper} sx={{ overflowX: "auto" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>Wire Name</TableCell>
              <TableCell>Cu %</TableCell>
              <TableCell>Photos</TableCell>
              <TableCell>Notes</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ color: "text.secondary" }}>
                  No wires saved yet
                </TableCell>
              </TableRow>
            ) : rows.map((r) => (
              <TableRow key={r.Wire_ID}>
                <TableCell>{r.Wire_ID}</TableCell>
                <TableCell>{r.WireName}</TableCell>
                <TableCell>{r.WirePercentage == null ? "-" : `${r.WirePercentage}%`}</TableCell>
                <TableCell>
                  <Chip
                    icon={<PhotoLibraryIcon />}
                    size="small"
                    label={r.ImageCount || 0}
                    color={r.ImageCount > 0 ? "primary" : "default"}
                    onClick={() => setGalleryWire({ Wire_ID: r.Wire_ID, WireName: r.WireName })}
                  />
                </TableCell>
                <TableCell sx={{ maxWidth: 240, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {r.Notes || ""}
                </TableCell>
                <TableCell align="right">
                  <Button size="small" onClick={() => editWire(r)}>Edit</Button>
                  <IconButton size="small" color="error" onClick={() => deleteWire(r.Wire_ID)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* === Gallery dialog === */}
      {galleryWire && (
        <WireGalleryDialog
          wire={galleryWire}
          onClose={() => { setGalleryWire(null); fetchRows(); }}
        />
      )}
    </Box>
  );
}

/* ========================================================================
 * Gallery dialog: list, upload, delete photos for one wire.
 * ====================================================================== */
function WireGalleryDialog({ wire, onClose }) {
  const [images, setImages] = useState([]);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/wireLibrary/${wire.Wire_ID}`);
      setImages(res.data?.images || []);
    } catch (err) {
      console.error("Failed to load gallery:", err);
    }
  }, [wire.Wire_ID]);

  useEffect(() => { load(); }, [load]);

  const onPick = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("images", f));
      await api.post(`/wireLibrary/${wire.Wire_ID}/images`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await load();
    } catch (err) {
      console.error("Upload failed:", err);
      alert("Upload failed: " + (err.response?.data?.error || err.message));
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removeImage = async (imgId) => {
    if (!window.confirm("Delete this photo?")) return;
    try {
      await api.delete(`/wireLibrary/${wire.Wire_ID}/images/${imgId}`);
      await load();
    } catch (err) {
      console.error("Delete failed:", err);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Photos — {wire.WireName}</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ mb: 2 }}>
          <Button
            variant="outlined"
            component="label"
            startIcon={busy ? <CircularProgress size={16} /> : <CloudUploadIcon />}
            disabled={busy}
          >
            {busy ? "Uploading..." : "Add Photos"}
            <input ref={fileRef} type="file" hidden multiple accept="image/*" onChange={onPick} />
          </Button>
          <Typography variant="caption" sx={{ ml: 2, color: "text.secondary" }}>
            Upload 3–5 photos per wire (different angles & lighting) for best matching.
          </Typography>
        </Box>

        {images.length === 0 ? (
          <Typography sx={{ color: "text.secondary" }}>
            No photos yet. Add at least one — more is better.
          </Typography>
        ) : (
          <Grid container spacing={1}>
            {images.map((img) => (
              <Grid key={img.Image_ID} size={{ xs: 6, sm: 4, md: 3 }}>
                <Box
                  sx={{
                    position: "relative",
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1,
                    overflow: "hidden",
                  }}
                >
                  <img
                    src={uploadUrl(img.ImagePath)}
                    alt=""
                    style={{ width: "100%", height: 140, objectFit: "cover", display: "block" }}
                  />
                  <IconButton
                    size="small"
                    sx={{ position: "absolute", top: 4, right: 4, bgcolor: "rgba(255,255,255,0.85)" }}
                    onClick={() => removeImage(img.Image_ID)}
                  >
                    <DeleteIcon fontSize="small" color="error" />
                  </IconButton>
                </Box>
              </Grid>
            ))}
          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
